/**
 * Sheets and dialogs render at the top of the document, not inside the card
 * that opened them, so they sit over the page band and the navigation bar
 * instead of being trapped in their card's stacking context.
 *
 * AND THEY BEHAVE LIKE DIALOGS (audit, 2026-10-12). Before this, a keyboard
 * or screen-reader user opening "Complete it" stayed focused on a button
 * behind the scrim, Tab walked off into the page underneath, and Escape did
 * nothing. Now, while a layer is open:
 *
 *   - focus moves into it (the first button, or the element marked
 *     [data-autofocus]) and goes back where it came from when it closes;
 *   - Tab and Shift+Tab cycle inside it;
 *   - Escape calls `onClose`, unless `busy` -- a half-sent save is not
 *     something to walk away from mid-request;
 *   - the page behind does not scroll.
 */

import { createPortal } from 'react-dom';
import { useEffect, useRef, type ReactNode } from 'react';

const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

export function Layer({
  children,
  onClose,
  busy = false,
}: {
  children: ReactNode;
  onClose?: () => void;
  busy?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const blocked = useRef(busy);
  blocked.current = busy;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const root = box.current;
    const first =
      (root?.querySelector('[data-autofocus]') as HTMLElement | null) ??
      (root?.querySelector(FOCUSABLE) as HTMLElement | null);
    first?.focus({ preventScroll: true });

    const scroll = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && close.current && !blocked.current) {
        event.stopPropagation();
        close.current();
        return;
      }
      if (event.key !== 'Tab' || !root) return;
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) return;
      const head = items[0]!;
      const tail = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === head) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && document.activeElement === tail) {
        event.preventDefault();
        head.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = scroll;
      if (before && document.contains(before)) before.focus({ preventScroll: true });
    };
  }, []);

  const content = <div ref={box} className="layer-root">{children}</div>;
  if (typeof document === 'undefined') return content;
  return createPortal(content, document.body);
}
